import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { FOOD_GROUP_LABELS } from '@shared/events'
import {
  GROUPS_BY_BUCKET,
  PLATE_BUCKET_LABELS,
  PLATE_TARGET,
  type DayPlate,
  type PlateBucket,
} from '@shared/plate'
import { COLOR_PORCION } from '../lib/platos'

const AVISO: Record<PlateBucket, { bajo: string; alto: string }> = {
  vegetal: { bajo: 'falta verdura y fruta', alto: 'casi todo verdura y fruta' },
  proteina: { bajo: 'falta proteína', alto: 'demasiada proteína' },
  cereal: { bajo: 'falta cereal', alto: 'demasiado cereal' },
}

const BUCKETS = Object.keys(PLATE_TARGET) as PlateBucket[]

/**
 * El plato del día: una barra con las tres porciones y las marcas de la
 * proporción de referencia.
 *
 * El detalle se enseña al tocar, en un rótulo de alto fijo encima del dibujo,
 * donde el dedo no lo tape y sin que la barra se mueva bajo el dedo.
 */
export function Plato({ plate, titulo = 'Equilibrio de hoy' }: { plate: DayPlate; titulo?: string }) {
  const [tocado, setTocado] = useState<PlateBucket | null>(null)
  const total = BUCKETS.reduce((sum, bucket) => sum + plate.buckets[bucket], 0)
  const desviados = BUCKETS.filter((bucket) => plate.status[bucket] !== 'ok')
  const rotuloActual =
    total === 0 ? 'Sin nada planificado todavía para hoy.' : rotulo(plate, tocado, desviados)

  return (
    <section className="rounded-3xl border border-border/70 bg-card px-4 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="label-caps font-sans text-muted-foreground">{titulo}</h2>
        {total > 0 && desviados.length === 0 && (
          <span className="label-caps rounded-full bg-filled px-2 py-1 text-sage-deep">equilibrado</span>
        )}
      </div>

      {/* Alto fijo: el rótulo no puede mover el dibujo al tocarlo. */}
      <div className="flex h-11 items-center text-sm text-foreground/80">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={rotuloActual}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            {rotuloActual}
          </motion.span>
        </AnimatePresence>
      </div>

      <div
        className="relative flex h-3.5 overflow-hidden rounded-full bg-surface"
        role="group"
        aria-label="Proporción del día"
      >
        {total > 0 &&
          BUCKETS.map((bucket) => {
            const share = plate.shares[bucket]
            if (share <= 0) return null
            return (
              <motion.button
                key={bucket}
                type="button"
                initial={false}
                animate={{ width: `${share * 100}%`, opacity: tocado && tocado !== bucket ? 0.4 : 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 30 }}
                className={`h-full border-r-2 border-card last:border-r-0 outline-none ${COLOR_PORCION[bucket]}`}
                aria-label={`${PLATE_BUCKET_LABELS[bucket]}, ${Math.round(share * 100)} por ciento`}
                onClick={() => setTocado((actual) => (actual === bucket ? null : bucket))}
              />
            )
          })}
      </div>

      <div className="relative mt-1 h-4 text-[0.65rem] text-muted-foreground" aria-hidden="true">
        <span className="absolute -translate-x-1/2" style={{ left: '50%' }}>
          50%
        </span>
        <span className="absolute -translate-x-1/2" style={{ left: '75%' }}>
          75%
        </span>
      </div>

      <ul className="mt-2 grid grid-cols-3 gap-2">
        {BUCKETS.map((bucket) => (
          <li key={bucket} className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`h-2 w-2 shrink-0 rounded-full ${COLOR_PORCION[bucket]}`} />
              <span className="truncate text-xs font-medium text-foreground/80">
                {PLATE_BUCKET_LABELS[bucket]}
              </span>
            </div>
            <p className="mt-0.5 pl-3.5 text-sm font-semibold tabular-nums text-foreground/70">
              {Math.round(plate.shares[bucket] * 100)}%
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                de {Math.round(PLATE_TARGET[bucket] * 100)}
              </span>
            </p>
          </li>
        ))}
      </ul>

      {(plate.informative.lacteo > 0 || plate.informative.grasa > 0) && (
        <p className="mt-3 text-xs text-muted-foreground">
          Además: {informativos(plate)}. No cuentan para la proporción.
        </p>
      )}

      {plate.dishesWithoutRecipe > 0 && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {plate.dishesWithoutRecipe === 1
            ? 'Un plato sin receta, así que no entra en la cuenta.'
            : `${plate.dishesWithoutRecipe} platos sin receta, así que no entran en la cuenta.`}
        </p>
      )}

      {plate.unweighedIngredients > 0 && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {plate.unweighedIngredients} ingrediente
          {plate.unweighedIngredients === 1 ? ' sin peso' : 's sin peso'}: no se cuenta lo que no se
          sabe.
        </p>
      )}
    </section>
  )
}

/** La versión de bolsillo para las fichas de la semana: sólo la barra. */
export function PlatoMini({ plate }: { plate: DayPlate }) {
  const total = BUCKETS.reduce((sum, bucket) => sum + plate.buckets[bucket], 0)
  const resumen =
    total === 0
      ? 'Sin datos para medir el equilibrio del día'
      : BUCKETS.map((b) => `${PLATE_BUCKET_LABELS[b]} ${Math.round(plate.shares[b] * 100)}%`).join(', ')

  return (
    <div className="flex h-1.5 gap-0.5 overflow-hidden rounded-full bg-surface" role="img" aria-label={resumen}>
      {total > 0 &&
        BUCKETS.map((bucket) =>
          plate.shares[bucket] > 0 ? (
            <motion.span
              key={bucket}
              initial={false}
              animate={{ width: `${plate.shares[bucket] * 100}%` }}
              transition={{ type: 'spring', stiffness: 200, damping: 30 }}
              className={`h-full ${COLOR_PORCION[bucket]}`}
            />
          ) : null,
        )}
    </div>
  )
}

function rotulo(plate: DayPlate, tocado: PlateBucket | null, desviados: PlateBucket[]): string {
  if (tocado) {
    const grupos = GROUPS_BY_BUCKET[tocado]
      .filter((group) => plate.grams[group] > 0)
      .map((group) => `${FOOD_GROUP_LABELS[group]} ${Math.round(plate.grams[group])} g`)
      .join(' · ')
    return grupos || `${PLATE_BUCKET_LABELS[tocado]}: nada todavía`
  }

  if (desviados.length === 0) return 'El día queda equilibrado.'

  const texto = desviados
    .map((bucket) => AVISO[bucket][plate.status[bucket] === 'bajo' ? 'bajo' : 'alto'])
    .join(', ')
  return texto.charAt(0).toUpperCase() + texto.slice(1) + '.'
}

function informativos(plate: DayPlate): string {
  const partes: string[] = []
  if (plate.informative.lacteo > 0) partes.push(`${Math.round(plate.informative.lacteo)} g de lácteo`)
  if (plate.informative.grasa > 0) partes.push(`${Math.round(plate.informative.grasa)} g de grasa`)
  return partes.join(' y ')
}
